import type { JSX } from "react";

export interface Recipient {
  name: string;
  role?: string;
  organisation?: string;
}

export interface Sender {
  name: string;
  role?: string;
  organisation?: string;
}

export interface Signer {
  name: string;
  title?: string;
}

export interface Advisor {
  item: string;
  firm: string;
}

export interface Contact {
  name: string;
  role?: string;
  email?: string;
  phone?: string;
}

export interface DocumentMetadata {
  letter_date?: string;
  target_company?: string;
  valuation?: string;
  recipient_bank?: string;
  recipients?: Recipient[];
  senders?: Sender[];
}

export interface ParagraphData {
  recipients?: Recipient[];
  bank?: string;
  closing_text?: string;
  signers?: Signer[];
  on_behalf?: string;
  title?: string;
  intro?: string;
  advisors?: Advisor[];
  contacts?: Contact[];
}

export interface Paragraph {
  type?: string;
  data: string | ParagraphData;
  style?: string;
  section?: string;
  fixed?: boolean;
}

export interface YamlDocumentData {
  metadata?: DocumentMetadata;
  paragraphs?: Paragraph[];
}

export interface MetadataFieldProps {
  icon: JSX.Element;
  label: string;
  value: string;
}

export interface ParagraphItemProps {
  paragraph: Paragraph;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}

const PREVIEW_LENGTH = 100;

const PREVIEW_BUILDERS: Record<string, (data: ParagraphData) => string> = {
  recipients: (data) => data.recipients?.map((r) => r.name).join(", ") || "Recipients",
  signatures: (data) => data.signers?.map((s) => s.name).join(", ") || "Signatures",
  "advisors-table": (data) => data.title || "Advisors Table",
  "contacts-table": (data) => data.title || "Contacts Table",
};

/** One-line summary shown while a paragraph is collapsed. */
export function previewText(paragraphType: string, data: Paragraph["data"]): string {
  if (paragraphType === "markdown") {
    const text = typeof data === "string" ? data : String(data || "");
    return text.length > PREVIEW_LENGTH ? text.substring(0, PREVIEW_LENGTH) + "..." : text;
  }

  const build = PREVIEW_BUILDERS[paragraphType];
  return build ? build(data as ParagraphData) : `${paragraphType} content`;
}
